import { useEffect, useRef } from 'react'

import { keys as lodashKeys } from 'lodash-es'

const keysOf = <T extends {}>(obj: T): Array<keyof T> => {
  return (lodashKeys(obj) as unknown) as Array<keyof T>
}

type ChangesObj<T> = {
  [k in keyof T]: { from?: T[k]; to: T[k] }
}

/**
 * A debugging tool to log which properties of an object changed between renders.
 * @param name A human-readable identifier for the hook call. Will be logged to console.
 * @param props The object to track changes in.
 */
export const useWhyDidYouUpdate = <T extends object>(
  name: string,
  props: T
) => {
  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  const previousProps = useRef<T>()
  useEffect(() => {
    if (previousProps.current) {
      // Get all keys from previous and current props
      const allKeys = keysOf({ ...previousProps.current, ...props })
      // Use this object to keep track of changed props
      const changesObj: {
        [k in keyof T]: { from?: T[keyof T]; to: T[keyof T] }
      } = {} as ChangesObj<T>
      // Iterate through keys
      allKeys.forEach((key) => {
        // If previous is different from current
        if (previousProps.current?.[key] !== props[key]) {
          // Add to changesObj
          changesObj[key] = {
            from: previousProps.current?.[key],
            to: props[key]
          }
        }
      })
      // If changesObj not empty then output to console
      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj)
      }
    }
    // Finally update previousProps with current props for next hook call
    previousProps.current = props
  })
}

export default useWhyDidYouUpdate
